import { useMemo } from 'react';
import { paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import SvgColor from 'src/components/svg-color';
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const { user } = useAuthContext();

  const data = useMemo(() => {
    if (!user) return [];

    const role = user.ent_chucvu?.Role;
    // Chỉ coi tài khoản là thuộc dự án khi ID dự án có giá trị thực tế.
    const hasProject = user?.ID_Duan != null;

    const isSpecialPosition = user.ID_Chucvu === 13;

    // Helper functions
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const createNavItem = (title: string, path: string, icon: any, children?: any[]) => ({
      title: t(title),
      path,
      icon,
      ...(children && {
        children: children.map((child) => {
          if (Array.isArray(child.children) && child.children.length > 0) {
            return {
              title: t(child.title),
              ...(child.path ? { path: child.path } : {}),
              children: child.children.map((subChild: any) => ({
                title: t(subChild.title),
                ...(subChild.path ? { path: subChild.path } : {}),
              })),
            };
          }
          return {
            title: t(child.title),
            ...(child.path ? { path: child.path } : {}),
          };
        }),
      }),
    });

    // Build Overview section
    const buildOverviewItems = () => {
      if (isSpecialPosition) {
        return [createNavItem('analytics', paths.dashboard.general.analytics, ICONS.analytics)];
      }

      if (hasProject && role !== 13) {
        return [
          createNavItem('analytics', paths.dashboard.general.analytics, ICONS.analytics),
          createNavItem(
            'statistical report',
            paths.dashboard.general.statistical_report,
            ICONS.file
          ),
        ];
      }

      if (role === 1 || role === 2) {
        return [
          createNavItem('analytics', paths.dashboard.general.analytics, ICONS.analytics),
          createNavItem(
            'statistical report',
            paths.dashboard.general.statistical_report,
            ICONS.file
          ),
        ];
      }

      if (role === 10 || role === 0 || role === 5) {
        return [createNavItem('management', paths.dashboard.general.management, ICONS.analytics)];
      }

      if (role === 4) {
        return [
          createNavItem(
            'managementDepartment',
            paths.dashboard.general.managementDepartment,
            ICONS.analytics
          ),
        ];
      }

      return [];
    };

    // Build Management section - Core items
    const buildManagementCoreItems = () => {
      // Role 3 với project: không có items
      if (role === 3 && hasProject) return [];

      // Có project nhưng không phải special position
      if (hasProject && !isSpecialPosition) {
        const items = [];

        // Admin roles có full access
        if (role === 1 || role === 2 || (role === 10 && hasProject)) {
          items.push(
            createNavItem('area', paths.dashboard.khuvuc.root, ICONS.folder, [
              { title: 'Danh sách khu vưc', path: paths.dashboard.khuvuc.root },
              { title: 'create', path: paths.dashboard.khuvuc.new },
            ]),
            createNavItem('article', paths.dashboard.hangmuc.root, ICONS.kanban, [
              { title: 'Danh sách hạng mục', path: paths.dashboard.hangmuc.root },
              { title: 'create', path: paths.dashboard.hangmuc.new },
            ]),
            createNavItem('calv', paths.dashboard.calv.root, ICONS.job, [
              { title: 'Danh sách ca', path: paths.dashboard.calv.root },
              { title: 'create', path: paths.dashboard.calv.new },
            ]),
            createNavItem('calv_cc', paths.dashboard.calv_cc.root, ICONS.job, [
              { title: 'Danh sách ca', path: paths.dashboard.calv_cc.root },
              { title: 'create', path: paths.dashboard.calv_cc.new },
            ]),
            createNavItem('chukyduan', paths.dashboard.chukyduan.root, ICONS.analytics, [
              { title: 'Danh sách chu kỳ', path: paths.dashboard.chukyduan.root },
              { title: 'create', path: paths.dashboard.chukyduan.new },
            ]),
            createNavItem(
              'phanquyenchecklist',
              paths.dashboard.phanquyenchecklist.root,
              ICONS.external,
              [
                { title: 'Danh sách phân ca', path: paths.dashboard.phanquyenchecklist.root },
                { title: 'create', path: paths.dashboard.phanquyenchecklist.new },
              ]
            ),
            createNavItem('checklist', paths.dashboard.checklist.root, ICONS.lock, [
              { title: 'Danh sách Checklist', path: paths.dashboard.checklist.root },
              { title: 'create', path: paths.dashboard.checklist.new },
              { title: 'calamviec', path: paths.dashboard.checklist.lists },
              { title: 'Ca trong ngày', path: paths.dashboard.checklist.catrongngay },
            ]),
            createNavItem('suco', paths.dashboard.sucongoai.root, ICONS.disabled, [
              { title: 'Danh sách sự cố', path: paths.dashboard.sucongoai.root },
            ])
          );
        } else {
          // Other roles với project: chỉ có checklist limited
          items.push(
            createNavItem('checklist', paths.dashboard.checklist.root, ICONS.lock, [
              { title: 'calamviec', path: paths.dashboard.checklist.lists },
              { title: 'Ca trong ngày', path: paths.dashboard.checklist.catrongngay },
            ])
          );
        }

        return items;
      }

      return [];
    };

    // Build Management section - Additional items (prepend)
    const buildManagementPrependItems = () => {
      const items = [];

      // Admin và có project (không phải role 3)
      if ((role === 1 || role === 2 || (hasProject && role !== 3)) && !isSpecialPosition) {
        items.push(
          createNavItem('Tài liệu phân hệ', paths.dashboard.tailieuphanhe.root, ICONS.blog, [
            { title: 'create', path: paths.dashboard.tailieuphanhe.new },
            { title: 'Danh sách tài liệu', path: paths.dashboard.tailieuphanhe.root },
          ]),
          createNavItem('building', paths.dashboard.toanha.root, ICONS.banking, [
            { title: 'create', path: paths.dashboard.toanha.new },
            { title: 'Danh sách tòa nhà', path: paths.dashboard.toanha.root },
          ]),
          createNavItem('tang', paths.dashboard.tang.root, ICONS.banking, [
            { title: 'create', path: paths.dashboard.tang.new },
            { title: 'Danh sách tầng', path: paths.dashboard.tang.root },
          ]),
          createNavItem('createaccount', paths.dashboard.createUser.root, ICONS.user, [
            { title: 'create', path: paths.dashboard.createUser.root },
            { title: 'Danh sách tài khoản', path: paths.dashboard.createUser.list },
          ])
        );
      }

      // PSH không có project
      if (role === 10 && !hasProject) {
        items.push(
          createNavItem('project', paths.dashboard.duan.root, ICONS.tour, [
            { title: 'create', path: paths.dashboard.duan.new },
            { title: 'Danh sách dự án', path: paths.dashboard.duan.root },
          ]),
          createNavItem('hsse', paths.dashboard.hsse.root, ICONS.analytics, [
            { title: 'Tổng hợp', path: paths.dashboard.hsse.admin },
            { title: 'Điện nước', path: paths.dashboard.hsse.dien_nuoc },
          ]),
          createNavItem('Điện', paths.dashboard.report.dien, ICONS.banking),
          createNavItem('Quản lý dịch vụ AN', paths.dashboard.anninh.root, ICONS.lock, [
            {
              title: 'Báo cáo ngày',
              path: paths.dashboard.anninh.baocao.root,
              children: [
                { title: 'Chấm công', path: paths.dashboard.anninh.baocao.chamcong },
                { title: 'Vi phạm', path: paths.dashboard.anninh.baocao.list_bc_loi },
                { title: 'Công cụ', path: paths.dashboard.anninh.baocao.list_bc_congcu },
                { title: 'Đào tạo giao ca', path: paths.dashboard.anninh.baocao.list_bc_daotao },
              ],
            },
            { title: 'Báo cáo thống kê', path: paths.dashboard.anninh.baocaothongke },
          ]),
          createNavItem('Bảo trì bảo dưỡng', paths.dashboard.baotribaoduong.root, ICONS.tour, [
            { title: 'Danh mục nhóm thiết bị', path: paths.dashboard.baotribaoduong.nhomthietbi },
            { title: 'Danh mục thiết bị', path: paths.dashboard.baotribaoduong.thietbi },
            ...(hasProject ? [
              { title: 'Thiết bị dự án', path: paths.dashboard.baotribaoduong.thietbiduan },
              { title: 'Phiếu bảo trì', path: paths.dashboard.baotribaoduong.phieubaotri.root },
            ] : []),
          ]),
          createNavItem('phanhe', paths.dashboard.phanhe.root, ICONS.menuItem, [
            { title: 'Tổng hợp', path: paths.dashboard.phanhe.admin },
          ]),
          createNavItem('baocaovitri', paths.dashboard.general.location, ICONS.analytics, [
            { title: 'Danh sách vị trí', path: paths.dashboard.general.location },
          ]),
          createNavItem('createaccount', paths.dashboard.quanlytaikhoan.root, ICONS.user, [
            { title: 'create', path: paths.dashboard.quanlytaikhoan.root },
            { title: 'Danh sách tài khoản', path: paths.dashboard.quanlytaikhoan.list },
            { title: 'user_error', path: paths.dashboard.quanlytaikhoan.error },
            { title: 'resetPassWord', path: paths.dashboard.quanlytaikhoan.resetPassWord },
          ]),
          createNavItem('Quản lý tài sản', 'https://qlts.pmcweb.vn/', ICONS.external),
          createNavItem('Báo cáo tháng', paths.dashboard.baocaothang.root, ICONS.tour)
        );
      }

      // PSH có project
      if (role === 10 && hasProject) {
        items.unshift(
          createNavItem('project', paths.dashboard.duan.root, ICONS.tour, [
            { title: 'create', path: paths.dashboard.duan.new },
            { title: 'Danh sách dự án', path: paths.dashboard.duan.root },
          ]),
          createNavItem('baocaovitri', paths.dashboard.general.location, ICONS.analytics, [
            { title: 'list', path: paths.dashboard.general.location },
          ])
        );

      }

      // Role 4
      if (role === 4) {
        items.unshift(
          createNavItem('project', paths.dashboard.duan.root, ICONS.tour, [
            { title: 'Danh sách dự án', path: paths.dashboard.duan.root },
          ])
        );
      }

      // Role 1 với arr_Duan
      if (role === 1) {
        items.unshift(
          createNavItem('project', paths.dashboard.duan.root, ICONS.tour, [
            { title: 'Danh sách dự án', path: paths.dashboard.duan.root },
          ])
        )
      }

      // Role 5 với arr_Duan
      if (role === 5 && user.arr_Duan !== null) {
        items.unshift(
          createNavItem('project', paths.dashboard.duan.root, ICONS.tour, [
            { title: 'Danh sách dự án', path: paths.dashboard.duan.root },
          ])
        );
      }

      return items;
    };

    // Build Management section - Additional items (append)
    const buildManagementAppendItems = () => {
      const items = [];

      // Role 5 với arr_Duan
      if (role === 5 && user.arr_Duan !== null) {
        items.push(
          createNavItem('Quản lý dịch vụ AN', paths.dashboard.anninh.root, ICONS.tour, [
            {
              title: 'Báo cáo ngày',
              path: paths.dashboard.anninh.baocao.root,
              children: [
                { title: 'Chấm công', path: paths.dashboard.anninh.baocao.chamcong },
                { title: 'Vi phạm', path: paths.dashboard.anninh.baocao.list_bc_loi },
                { title: 'Công cụ', path: paths.dashboard.anninh.baocao.list_bc_congcu },
                { title: 'Đào tạo giao ca', path: paths.dashboard.anninh.baocao.list_bc_daotao },
              ],
            },
            { title: 'Báo cáo thống kê', path: paths.dashboard.anninh.baocaothongke },
          ]),
          createNavItem('Bảo trì bảo dưỡng', paths.dashboard.baotribaoduong.root, ICONS.tour, [
            { title: 'Danh mục nhóm thiết bị', path: paths.dashboard.baotribaoduong.nhomthietbi },
            { title: 'Danh mục thiết bị', path: paths.dashboard.baotribaoduong.thietbi },
            ...(hasProject ? [{ title: 'Thiết bị dự án', path: paths.dashboard.baotribaoduong.thietbiduan }] : []),
          ])
        );
      }

      return items;
    };

    // Build Overview additional items
    const buildOverviewAdditionalItems = () => {
      const items = [];

      // HSSE items
      if ((role === 1 || role === 2 || role === 3) && !isSpecialPosition) {
        if (role === 1) {
          items.push(
            createNavItem('hsse', paths.dashboard.hsse.root, ICONS.tour, [
              { title: 'phanquyen', path: paths.dashboard.hsse.phanquyen },
              { title: 'Dữ liệu', path: paths.dashboard.hsse.root },
              { title: 'create', path: paths.dashboard.hsse.new },
            ]),
            createNavItem('Bể bơi', paths.dashboard.beboi.root, ICONS.tour, [
              { title: 'Dữ liệu', path: paths.dashboard.beboi.root },
            ]),
            createNavItem('Thang máy', paths.dashboard.thangmay.root, ICONS.tour, [
              { title: 'Dữ liệu', path: paths.dashboard.thangmay.root },
            ]),
            createNavItem('Đăng ký thi công', paths.dashboard.nhathau.root, ICONS.tour, [
              { title: 'Danh sách', path: paths.dashboard.nhathau.root },
            ]),
            createNavItem('Điện', paths.dashboard.report.dien, ICONS.tour)
          );
        } else {
          items.push(
            createNavItem('hsse', paths.dashboard.hsse.root, ICONS.tour, [
              { title: 'Dữ liệu', path: paths.dashboard.hsse.root },
              { title: 'create', path: paths.dashboard.hsse.new },
            ]),
            createNavItem('Bể bơi', paths.dashboard.beboi.root, ICONS.tour, [
              { title: 'Dữ liệu', path: paths.dashboard.beboi.root },
            ]),
            createNavItem('Đăng ký thi công', paths.dashboard.nhathau.root, ICONS.tour, [
              { title: 'Danh sách', path: paths.dashboard.nhathau.root },
            ]),
            createNavItem('Điện', paths.dashboard.report.dien, ICONS.tour)
          );
        }
      }

      // PSH với project
      if (role === 10 && hasProject && !isSpecialPosition) {
        items.push(
          createNavItem('hsse', paths.dashboard.hsse.root, ICONS.tour, [
            { title: 'phanquyen', path: paths.dashboard.hsse.phanquyen },
            { title: 'Dữ liệu', path: paths.dashboard.hsse.root },
            { title: 'create', path: paths.dashboard.hsse.new },
          ]),
          createNavItem('Bể bơi', paths.dashboard.beboi.root, ICONS.tour, [
            { title: 'Dữ liệu', path: paths.dashboard.beboi.root },
          ]),
          createNavItem('Đăng ký thi công', paths.dashboard.nhathau.root, ICONS.tour, [
            { title: 'Danh sách', path: paths.dashboard.nhathau.root },
          ]),
          createNavItem('Quản lý dịch vụ AN', paths.dashboard.anninh.root, ICONS.tour, [
            {
              title: 'Báo cáo ngày',
              path: paths.dashboard.anninh.baocao.root,
              children: [
                { title: 'Chấm công', path: paths.dashboard.anninh.baocao.chamcong },
                { title: 'Vi phạm', path: paths.dashboard.anninh.baocao.list_bc_loi },
                { title: 'Công cụ', path: paths.dashboard.anninh.baocao.list_bc_congcu },
                { title: 'Đào tạo giao ca', path: paths.dashboard.anninh.baocao.list_bc_daotao },
              ],
            },
            { title: 'Báo cáo thống kê', path: paths.dashboard.anninh.baocaothongke },
          ]),
          createNavItem('Bảo trì bảo dưỡng', paths.dashboard.baotribaoduong.root, ICONS.tour, [
            { title: 'Danh mục nhóm thiết bị', path: paths.dashboard.baotribaoduong.nhomthietbi },
            { title: 'Danh mục thiết bị', path: paths.dashboard.baotribaoduong.thietbi },
            ...(hasProject ? [
              { title: 'Thiết bị dự án', path: paths.dashboard.baotribaoduong.thietbiduan },
              { title: 'Phiếu bảo trì', path: paths.dashboard.baotribaoduong.phieubaotri.root },
            ] : []),
          ]),
          createNavItem('p0', paths.dashboard.p0.root, ICONS.tour, [
            { title: 'phanquyen', path: paths.dashboard.p0.phanquyen },
            { title: 'Dữ liệu', path: paths.dashboard.p0.root },
            { title: 'create', path: paths.dashboard.p0.new },
          ]),
          createNavItem('Điện', paths.dashboard.report.dien, ICONS.tour)
        );
      }

      // P0 items
      if ((role === 1 || role === 2 || role === 3) && !isSpecialPosition) {
        if (role === 1) {
          items.push(
            createNavItem('p0', paths.dashboard.p0.root, ICONS.tour, [
              { title: 'phanquyen', path: paths.dashboard.p0.phanquyen },
              { title: 'Dữ liệu', path: paths.dashboard.p0.root },
              { title: 'create', path: paths.dashboard.p0.new },
            ])
          );
        } else {
          items.push(
            createNavItem('p0', paths.dashboard.p0.root, ICONS.tour, [
              { title: 'Dữ liệu', path: paths.dashboard.p0.root },
              { title: 'create', path: paths.dashboard.p0.new },
            ])
          );
        }
        items.push(
          createNavItem('Quản lý dịch vụ AN', paths.dashboard.anninh.root, ICONS.tour, [
            {
              title: 'Báo cáo ngày',
              path: paths.dashboard.anninh.baocao.root,
              children: [
                { title: 'Chấm công', path: paths.dashboard.anninh.baocao.chamcong },
                { title: 'Vi phạm', path: paths.dashboard.anninh.baocao.list_bc_loi },
                { title: 'Công cụ', path: paths.dashboard.anninh.baocao.list_bc_congcu },
                { title: 'Đào tạo giao ca', path: paths.dashboard.anninh.baocao.list_bc_daotao },
              ],
            },
            { title: 'Báo cáo thống kê', path: paths.dashboard.anninh.baocaothongke },
          ]),
          createNavItem('Bảo trì bảo dưỡng', paths.dashboard.baotribaoduong.root, ICONS.tour, [
            { title: 'Danh mục nhóm thiết bị', path: paths.dashboard.baotribaoduong.nhomthietbi },
            { title: 'Danh mục thiết bị', path: paths.dashboard.baotribaoduong.thietbi },
            ...(hasProject ? [
              { title: 'Thiết bị dự án', path: paths.dashboard.baotribaoduong.thietbiduan },
              { title: 'Phiếu bảo trì', path: paths.dashboard.baotribaoduong.phieubaotri.root },
            ] : []),
          ])
        );
      }

      // Người thuộc dự án xem báo cáo tại đây; full admin đã có mục riêng bên quản lý.
      if (hasProject) {
        items.push(
          createNavItem('Báo cáo tháng', paths.dashboard.baocaothang.root, ICONS.tour)
        );
      }

      if (role === 5 && hasProject) {
        items.push(
          createNavItem('p0', paths.dashboard.p0.root, ICONS.tour, [
            { title: 'phanquyen', path: paths.dashboard.p0.phanquyen },
            { title: 'Dữ liệu', path: paths.dashboard.p0.root },
            { title: 'create', path: paths.dashboard.p0.new },
          ]),
          createNavItem('hsse', paths.dashboard.hsse.root, ICONS.tour, [
            { title: 'Dữ liệu', path: paths.dashboard.hsse.root },
            { title: 'create', path: paths.dashboard.hsse.new },
          ]),
          createNavItem('Bể bơi', paths.dashboard.beboi.root, ICONS.tour, [
            { title: 'Dữ liệu', path: paths.dashboard.beboi.root },
          ]),
          createNavItem('Đăng ký thi công', paths.dashboard.nhathau.root, ICONS.tour, [
            { title: 'Danh sách', path: paths.dashboard.nhathau.root },
          ]),
          createNavItem('Quản lý dịch vụ AN', paths.dashboard.anninh.root, ICONS.tour, [
            {
              title: 'Báo cáo ngày',
              path: paths.dashboard.anninh.baocao.root,
              children: [
                { title: 'Chấm công', path: paths.dashboard.anninh.baocao.chamcong },
                { title: 'Vi phạm', path: paths.dashboard.anninh.baocao.list_bc_loi },
                { title: 'Công cụ', path: paths.dashboard.anninh.baocao.list_bc_congcu },
                { title: 'Đào tạo giao ca', path: paths.dashboard.anninh.baocao.list_bc_daotao },
              ],
            },
            { title: 'Báo cáo thống kê', path: paths.dashboard.anninh.baocaothongke },
          ]),
          createNavItem('Bảo trì bảo dưỡng', paths.dashboard.baotribaoduong.root, ICONS.tour, [
            { title: 'Danh mục nhóm thiết bị', path: paths.dashboard.baotribaoduong.nhomthietbi },
            { title: 'Danh mục thiết bị', path: paths.dashboard.baotribaoduong.thietbi },
            ...(hasProject ? [{ title: 'Thiết bị dự án', path: paths.dashboard.baotribaoduong.thietbiduan }] : []),
          ])
        );
      }

      return items;
    };

    // Assemble navigation data
    const overviewItems = buildOverviewItems();
    const overviewAdditionalItems = buildOverviewAdditionalItems();

    const managementPrependItems = buildManagementPrependItems();
    const managementCoreItems = buildManagementCoreItems();
    const managementAppendItems = buildManagementAppendItems();

    const navigationData = [
      {
        subheader: t('overview'),
        items: [...overviewItems, ...overviewAdditionalItems],
      },
      {
        subheader: t('management'),
        items: [...managementPrependItems, ...managementCoreItems, ...managementAppendItems],
      },
      {
        subheader: t('profile'),
        items: [
          createNavItem('user', paths.dashboard.userAdmin.root, ICONS.user, [
            { title: 'account', path: paths.dashboard.userAdmin.root },
          ]),
        ],
      },
    ];

    return navigationData;
  }, [t, user]);

  return data;
}
